import type { ReactNode, VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import styles from './Preview.module.less';
import HTMLPreviewContent from './HTMLPreviewContent';
import ReportViewer from '../../../../../../ReportViewer';
import { previewFormatMap, previewTitleMap, reportTypeDefaultFormatMap } from '../../constants';
import type { ReportCategory } from '../../../../shared/types';
import Loader from '../../../../../../generic/Loader';
import { ErrorAlert, icons } from '../../../../../../generic';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../../types';
import ActionButtons from '../../../ActionButtons';
import Flex from '../../../../../../generic/Flex';
import TooltipIcon from '../../../../../../generic/TooltipIcon';
import { ReportCategories } from '../../../utils';

type Props = {
  previewURL: string | null;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  /**
   * Accept a ReactNode to display the published report status.
   * This is used for release group level reports.
   */
  publishStatus?: ReactNode;
};

const Preview: VoidFunctionComponent<Props> = ({
  previewURL,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  publishStatus,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const formatText = previewFormatMap[reportType][format || 'default'];
  const htmlPreviewFormat = format || reportTypeDefaultFormatMap[reportType];
  const isLicensing = reportType === ReportCategories.Licensing;
  const isSBOM = reportType === ReportCategories.SBOM;
  const isFixPlan = reportType === ReportCategories.RemediationGuidance;
  const isVulnerability = reportType === ReportCategories.Vulnerability;
  // Show the loader and clear the error message when the preview URL changes
  // Some reports may take a while to generate, so we want to show the loader while waiting
  useEffect(() => {
    setIsLoading(true);
    setErrorMessage('');
  }, [previewURL]);

  if (!previewURL) {
    return null;
  }

  return (
    <>
      <div className={styles.header}>
        <Flex gap="4px" direction="column" align="flex-start">
          <div className={styles.previewTitle}>{previewTitleMap[reportType]}</div>
          {publishStatus}
        </Flex>
        {isVulnerability && (
          <ActionButtons
            reportType={reportType}
            format={format}
            includeOptions={includeOptions}
            dependencyMetadataOptions={dependencyMetadataOptions}
          />
        )}
      </div>
      {errorMessage && <ErrorAlert status={{ errors: [errorMessage] }} />}
      <ReportViewer>
        <ReportViewer.Format>
          <Flex gap="8px">
            <span>{formatText}</span>
            {isLicensing && (
              <TooltipIcon
                id="preview-tooltip"
                icon={icons.circleQuestion}
                tooltip="Previews only show a limited number of dependencies and licenses."
              />
            )}
            {isSBOM && (
              <TooltipIcon
                id="preview-tooltip"
                icon={icons.circleQuestion}
                tooltip="Previews only show a limited number of results."
              />
            )}
            {isFixPlan && (
              <TooltipIcon
                id="preview-tooltip"
                icon={icons.circleQuestion}
                tooltip="Preview report based on partial data."
              />
            )}
          </Flex>
        </ReportViewer.Format>
        <ReportViewer.Content>
          {isLoading && <Loader />}
          <div className={styles.reportContent}>
            <HTMLPreviewContent
              format={htmlPreviewFormat === 'PDF' ? 'PDF' : 'TXT'}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              setErrorMessage={setErrorMessage}
              previewURL={previewURL}
            />
          </div>
        </ReportViewer.Content>
      </ReportViewer>
    </>
  );
};

export default Preview;
